import { entitlementDenialMessage } from '@/lib/entitlementDenial';
import i18n from '@/i18n';

/**
 * Best-effort human-readable message from a failed generated-SDK call.
 *
 * The hey-api mutations reject with the response body when the backend
 * returns a structured error:
 *   - Powertools raises      → `{ statusCode, message }`
 *   - FastAPI HTTPException   → `{ detail: string }`
 *   - Request validation (422) → `{ detail: Array<{ loc, msg, type }> }`
 * and with a plain `Error` on network-level failures.
 *
 * The 422 case is why callers used to surface a bare `[object Object]`: a
 * Pydantic body-validation failure (e.g. the wrap external-flow "requires
 * element_type='hex'" guard) puts an *array* of error objects on `detail`,
 * which isn't a string, so the old code fell through to `String(error)`.
 * Join the per-error `msg` fields instead, stripping Pydantic's boilerplate
 * "Value error, " prefix (and skipping any that strip to empty, so a bare
 * `ValueError('')` can't produce a blank message or a leading "; ") so the
 * actionable sentence shows through.
 *
 * Returns `''` for an object error with no extractable message (a msg-less 422
 * body, a dict/opaque object) so callers apply their own fallback instead of
 * surfacing `[object Object]`; `String(error)` is used only for non-object
 * inputs (already readable). See ENG-1257.
 *
 * The compute gate's seat/subscription 403s (`{ error, product }`, no prose)
 * are read first and turned into a translated sentence, so every submit
 * surface that already routes through here says why instead of falling back to
 * its generic failure (ENG-3091, `@/lib/entitlementDenial`). The same goes for
 * a run too large to read (`{ error: 'artifact_too_large' }`, ENG-3123).
 */
export function apiErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const denial = entitlementDenialMessage(error);
    if (denial) return denial;
    const rec = error as Record<string, unknown>;
    // A run whose artifact is bigger than the backend was sized for. Said in
    // the reader's language, not the server's sentence — which named an S3
    // key and reached the report's charts verbatim (ENG-3123).
    if (rec.error === 'artifact_too_large')
      return i18n.t('compCommon.apiRefusal.artifactTooLarge');
    if (typeof rec.message === 'string') return rec.message;
    if (typeof rec.detail === 'string') return rec.detail;
    if (Array.isArray(rec.detail)) {
      const msgs = (rec.detail as unknown[]).flatMap((d) => {
        const msg =
          typeof d === 'object' && d !== null
            ? (d as { msg?: unknown }).msg
            : undefined;
        if (typeof msg !== 'string') return [];
        const cleaned = msg.replace(/^value error,\s*/i, '').trim();
        return cleaned ? [cleaned] : [];
      });
      if (msgs.length > 0) return msgs.join('; ');
    }
    // Object error with no extractable human message — a msg-less 422 body
    // ({detail:[{loc,type}]}: a schema error, or a BE that still strips msg) or
    // an opaque object. Return '' so each caller applies its own fallback (e.g.
    // useSubmitMeshJob's "Failed to start mesh generation") rather than
    // surfacing "[object Object]" (ENG-1257).
    return '';
  }
  // Non-object input (string, number, …) is already readable.
  return String(error);
}
